import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "react-toastify";
import CabiBubble from "../../components/Kids/CabiBubble";
import CabiMascot from "../../components/Kids/CabiMascot";
import Button from "../../components/Kids/Button";
import Seo from "../../components/Seo";
import SEO from "../../seo/pages.json";

const API = process.env.REACT_APP_API_URL;

export default function ForgotPassword() {
  const navigate = useNavigate();

  const [step, setStep] = useState(1); // 1 = request code, 2 = enter code + new password
  const [email, setEmail] = useState("");
  const [otp, setOtp] = useState("");
  const [password, setPassword] = useState("");
  const [passwordConfirmation, setPasswordConfirmation] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const requestCode = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/api/kids/parent/forgot-password`, { email });
      toast.success("If that email is registered, a reset code is on its way!");
      setStep(2);
    } catch (err) {
      toast.error(err.response?.data?.message || "Something went wrong. Try again shortly.");
    } finally {
      setLoading(false);
    }
  };

  const resetPassword = async (e) => {
    e.preventDefault();
    setError("");
    if (password !== passwordConfirmation) {
      setError("Passwords don't match.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/api/kids/parent/reset-password`, {
        email, otp,
        password, password_confirmation: passwordConfirmation,
      });
      toast.success("Password reset! Please log in with your new password.");
      navigate("/kids/login");
    } catch (err) {
      setError(err.response?.data?.message || "That code is invalid or has expired.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="min-h-screen flex flex-col items-center justify-center p-4 font-body"
      style={{ background: "linear-gradient(150deg,#FFF6EE,#FFF9F4 55%,#fff)" }}
    >
      <Seo
        {...SEO["/kids/forgot-password"]}
        path="/kids/forgot-password"
      />
      <style>{`
        @keyframes slideUp { from{transform:translateY(20px);opacity:0} to{transform:translateY(0);opacity:1} }
        .slide-up { animation: slideUp 0.4s ease both; }
      `}</style>

      <div className="mb-6 text-center">
        <Link to="/kids" className="font-display font-semibold text-2xl tracking-tight">
          <span className="text-brand">Cabil</span> <span className="text-gold">Kids</span>
        </Link>
      </div>

      <div className="w-full max-w-md">
        {step === 1 ? (
          <>
            <CabiBubble
              mood="waving"
              theme="light"
              size={72}
              className="mb-5 px-2"
              text="No worries — it happens! Enter your email and we'll send you a reset code. 🔑"
            />
            <div className="bg-white rounded-sheet shadow-sheet p-8 slide-up">
              <h1 className="font-display font-semibold text-2xl text-ink mb-1">Forgot your password?</h1>
              <p className="text-muted text-sm mb-6">Enter the email on your Cabil Kids parent account.</p>

              <form onSubmit={requestCode} className="space-y-4">
                <div>
                  <label className="block font-body font-extrabold text-[13px] text-ink mb-1.5">Email</label>
                  <input
                    type="email" value={email} onChange={(e) => setEmail(e.target.value)} required
                    className="w-full border border-[#E5DED4] rounded-xl px-4 py-3 text-ink font-body font-semibold text-[15px] focus:ring-2 focus:ring-brand/30 focus:border-brand outline-none transition"
                  />
                </div>
                <Button type="submit" disabled={loading} className="w-full">
                  {loading ? "Sending…" : "Send reset code 📧"}
                </Button>
              </form>

              <p className="text-center text-sm text-muted font-body font-semibold mt-4">
                Remembered it? <Link to="/kids/login" className="text-brand font-extrabold">Log in</Link>
              </p>
            </div>
          </>
        ) : (
          <>
            <div className="bg-white rounded-sheet shadow-sheet p-8 text-center slide-up">
              <div
                className="w-[110px] h-[110px] rounded-full mx-auto mb-3 flex items-center justify-center"
                style={{ background: "linear-gradient(150deg,#FFB47A,#EE5F2B)" }}
              >
                <CabiMascot mood="celebrating" size={70} />
              </div>
              <h1 className="font-display font-semibold text-2xl text-ink mb-2">Check your email!</h1>
              <p className="text-muted font-body font-semibold mb-6">
                We've sent a 6-digit code to <span className="text-ink font-extrabold">{email}</span>.
                Enter it below along with your new password.
              </p>

              <form onSubmit={resetPassword} className="space-y-4 text-left">
                <div>
                  <label className="block font-body font-extrabold text-[13px] text-ink mb-1.5">Reset code</label>
                  <input
                    type="text" inputMode="numeric" autoComplete="one-time-code" maxLength={6}
                    value={otp}
                    onChange={(e) => { setOtp(e.target.value.replace(/\D/g, "").slice(0, 6)); setError(""); }}
                    placeholder="000000"
                    className="w-full text-center border border-[#E5DED4] rounded-xl px-4 py-3.5 text-ink font-display font-semibold text-[28px] tracking-[0.5em] focus:ring-2 focus:ring-brand/30 focus:border-brand outline-none transition"
                  />
                </div>
                <div>
                  <label className="block font-body font-extrabold text-[13px] text-ink mb-1.5">New password</label>
                  <input
                    type="password" minLength={8} value={password} onChange={(e) => setPassword(e.target.value)} required
                    className="w-full border border-[#E5DED4] rounded-xl px-4 py-3 text-ink font-body font-semibold text-[15px] focus:ring-2 focus:ring-brand/30 focus:border-brand outline-none transition"
                  />
                  <p className="text-faint text-xs font-body font-semibold mt-1">At least 8 characters</p>
                </div>
                <div>
                  <label className="block font-body font-extrabold text-[13px] text-ink mb-1.5">Confirm new password</label>
                  <input
                    type="password" value={passwordConfirmation} onChange={(e) => setPasswordConfirmation(e.target.value)} required
                    className="w-full border border-[#E5DED4] rounded-xl px-4 py-3 text-ink font-body font-semibold text-[15px] focus:ring-2 focus:ring-brand/30 focus:border-brand outline-none transition"
                  />
                </div>
                {error && <p className="text-danger text-xs font-body font-semibold">{error}</p>}
                <Button type="submit" disabled={loading || otp.length !== 6} className="w-full">
                  {loading ? "Resetting…" : "Reset password 🔑"}
                </Button>
              </form>

              <button onClick={requestCode} disabled={loading}
                className="w-full text-center text-brand font-body font-extrabold text-sm hover:underline transition disabled:opacity-60 mt-4">
                Resend code
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
